import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { B2BAllowedAuthMethods } from '@stytch/react/b2b';
import { useParams } from 'react-router';

import { useOrganizationBySlug } from '@/api/organization';
import EmailSignInForm from '@/app/sign-in/EmailSignInForm';
import SignInPanel from '@/app/sign-in/SignInPanel';
import { PageSpinner } from '@/components/loaders';
import { FullPage } from '@/components/page';
import { Link, Text } from '@/components/typography';
import { setTenantPendingSignInMethod } from '@/helper/user-preferences';
import useDeferredLoading from '@/hooks/useDeferredLoading';
import { ROUTES } from '@/router';

import InstanceError from '../InstanceError';
import TenantAuthenticate from '../TenantAuthenticate';

const InstanceSignInEmail = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { organization, isLoading } = useOrganizationBySlug(orgSlug);
  const showLoading = useDeferredLoading(isLoading);

  const instanceSignInPath = ROUTES.signInInst.path.replace(':orgSlug', orgSlug ?? '');
  const passwordSignInPath = ROUTES.signInInstPassword.path.replace(':orgSlug', orgSlug ?? '');

  // Check if password is allowed for this organization
  const allowedMethods: B2BAllowedAuthMethods[] = organization?.allowed_auth_methods || [];
  const authMethodsMode = organization?.auth_methods;
  const isPasswordAllowed =
    authMethodsMode === 'ALL_ALLOWED' || allowedMethods.includes('password');

  if (!orgSlug) {
    return (
      <InstanceError
        title={t`Instance Not Found`}
        message={<Trans>No instance specified.</Trans>}
      />
    );
  }

  if (isLoading && showLoading) {
    return <PageSpinner />;
  }

  if (isLoading) {
    return null;
  }

  if (!organization) {
    return (
      <InstanceError
        title={t`Instance Not Found`}
        message={
          <Trans>The instance "{orgSlug}" was not found or you don't have access to it.</Trans>
        }
      />
    );
  }

  return (
    <FullPage title={t`Sign In with Email`}>
      <TenantAuthenticate orgSlug={orgSlug}>
        <SignInPanel
          title={t`Sign In with Email`}
          description={
            <Trans>
              We'll send a one-time access link to your business email so you can sign in to{' '}
              {organization.organization_name} without a password.
            </Trans>
          }
          footer={
            <>
              {isPasswordAllowed && (
                <Text variant="caption1" color="grey">
                  <Trans>
                    Already have a password?{' '}
                    <Link variant="caption1" color="aqua" to={passwordSignInPath}>
                      {t`Sign in with password`}
                    </Link>
                  </Trans>
                </Text>
              )}
              <Text variant="caption1" color="grey">
                <Trans>
                  Prefer a different method?{' '}
                  <Link variant="caption1" color="aqua" to={instanceSignInPath}>
                    Sign in another way
                  </Link>
                </Trans>
              </Text>
            </>
          }
          showGap
        >
          <EmailSignInForm
            organizationId={organization.organization_id}
            onStartAuth={() => {
              if (orgSlug) {
                setTenantPendingSignInMethod(orgSlug, 'email');
              }
            }}
          />
        </SignInPanel>
      </TenantAuthenticate>
    </FullPage>
  );
};

export default InstanceSignInEmail;
